<template>
  <ProgressBar v-if="dataIsLoading" :class="{ 'mt-4': currentView !== 'map' }" :value="hotelsLoading.value" :show-value="false"></ProgressBar>
  
  <!-- Floating View Toggle - visible on all screens -->
  <div v-if="currentView === 'list'" class="floating-view-toggle in-list-view">
    <ViewToggle
      v-model="currentView"
      @view-changed="onViewChanged"
    />
  </div>
  
  <div :class="['page-container', { 'map-view-active': currentView === 'map' }]">
    <!-- Breadcrumbs for list view -->
    <Breadcrumbs v-if="currentView === 'list'" :breadcrumbs="breadcrumbs" class="mt-4"/>
    
    <div v-if="currentView === 'list'" class="list-layout">
      <div class="sidebar-wrapper">
        <HotelsFilters class="on-page"/>
      </div>
      <div class="results-listing-wrapper">
        <!-- Static Hotels Banner - shown when no dates selected -->
        <div
          v-if="!searchStore.isDynamicHotels && (searchStore.hotelsProducts?.length > 0 || searchStore.hotelProductsFirstPage?.length > 0)"
          class="static-hotels-banner"
          @click="openSearchPanel"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 5V15M5 10H15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Please pick your stay dates to view current hotel availability and rates
        </div>
        
        <!-- Error banner hidden - hotels may be partially loaded when errors occur
        <div
          v-if="searchStore.hotelSearchError"
          class="hotel-error-banner"
          role="alert"
        >
          <div>{{ searchStore.hotelSearchError }}</div>
          <button class="btn btn-secondary btn-size-m" @click="onRetrySearch">{{ $t('Try again') }}</button>
        </div>
        -->
        <div class="results-listing-header">
          <h1>
            {{searchStore.selectedCity?.name}}<span v-if="searchStore.total > 0">: {{ searchStore.total }} {{$t('properties found')}}</span>
            <span v-if="searchStore.searchInProgress" class="loading-indicator">
              <span class="loading-dots">
                <span class="dot"></span>
                <span class="dot"></span>
                <span class="dot"></span>
              </span>
            </span>
          </h1>
          <div class="header-controls">
            <Sorting class="show-desktop" />
            <button @click="showMobileFiltersDialog = true" class="btn btn-secondary btn-size-l show-not-desktop">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.7083 9.99999H7.41246M3.77829 9.99999H2.29163M3.77829 9.99999C3.77829 9.51818 3.96969 9.0561 4.31038 8.71541C4.65107 8.37472 5.11315 8.18332 5.59496 8.18332C6.07677 8.18332 6.53885 8.37472 6.87954 8.71541C7.22023 9.0561 7.41163 9.51818 7.41163 9.99999C7.41163 10.4818 7.22023 10.9439 6.87954 11.2846C6.53885 11.6253 6.07677 11.8167 5.59496 11.8167C5.11315 11.8167 4.65107 11.6253 4.31038 11.2846C3.96969 10.9439 3.77829 10.4818 3.77829 9.99999ZM17.7083 15.5058H12.9183M12.9183 15.5058C12.9183 15.9877 12.7264 16.4503 12.3857 16.7911C12.0449 17.1319 11.5827 17.3233 11.1008 17.3233C10.619 17.3233 10.1569 17.1311 9.81622 16.7904C9.47552 16.4497 9.28413 15.9876 9.28413 15.5058M12.9183 15.5058C12.9183 15.0239 12.7264 14.5621 12.3857 14.2214C12.0449 13.8806 11.5827 13.6892 11.1008 13.6892C10.619 13.6892 10.1569 13.8806 9.81622 14.2212C9.47552 14.5619 9.28413 15.024 9.28413 15.5058M9.28413 15.5058H2.29163M17.7083 4.49416H15.1208M11.4866 4.49416H2.29163M11.4866 4.49416C11.4866 4.01235 11.678 3.55027 12.0187 3.20958C12.3594 2.86889 12.8215 2.67749 13.3033 2.67749C13.5419 2.67749 13.7781 2.72448 13.9985 2.81578C14.2189 2.90707 14.4192 3.04089 14.5879 3.20958C14.7566 3.37827 14.8904 3.57854 14.9817 3.79895C15.073 4.01936 15.12 4.25559 15.12 4.49416C15.12 4.73272 15.073 4.96896 14.9817 5.18937C14.8904 5.40977 14.7566 5.61004 14.5879 5.77873C14.4192 5.94743 14.2189 6.08124 13.9985 6.17254C13.7781 6.26383 13.5419 6.31082 13.3033 6.31082C12.8215 6.31082 12.3594 6.11943 12.0187 5.77873C11.678 5.43804 11.4866 4.97597 11.4866 4.49416Z" stroke="#1985F9" stroke-width="2" stroke-miterlimit="10" stroke-linecap="round"/>
              </svg>
              Filters
            </button>
          </div>

          <DialogCloseOutside
              v-model="showMobileFiltersDialog"
              :showHeader="false"
              position="right"
              :block-scroll="true"
              :modal="true"
              :draggable="false"
              class="modal-container modal-container--filters custom-scrollbar"
              @close="showMobileFiltersDialog = false"
          >
            <HotelsFiltersMobile class="in-dialog" @close="showMobileFiltersDialog = false"/>
          </DialogCloseOutside>

        </div>
        
        <!-- List view -->
        <HotelsList v-if="currentView === 'list'"/>
      </div>
    </div>
    
    <!-- Below-the-fold SEO content - only in list view -->
    <div v-if="currentView === 'list'" class="seo-content-section">
      <CityInfoSection :city-slug="(route.params.city as string)" :preloaded-info="hotelCityInfo" />
      <NearbyCities :city-slug="(route.params.city as string)" />
    </div>

    <!-- Map view container (outside of the flex container for full width) -->
    <div
      v-if="currentView === 'map'"
      class="map-view-full-width"
      :style="{ height: mapViewportHeight, minHeight: mapViewportHeight }"
    >
      <ClientOnly>
        <div class="map-view-inner">
          <HotelsSplitView
            ref="hotelsSplitViewRef"
            :map-hotels="mapHotels"
            :map-center="mapCenter"
            :poi-location="poiLocation"
            :breadcrumbs="breadcrumbs"
            :city-name="searchStore.selectedCity?.name"
            :total-count="searchStore.total"
            :current-view="currentView"
            @hotel-selected="onHotelSelected"
            @map-moved="onMapMoved"
            @view-changed="onViewChanged"
            @show-mobile-filters="showMobileFiltersDialog = true"
          >
            <template #view-toggle>
              <div class="floating-view-toggle in-map-view">
                <ViewToggle
                  v-model="currentView"
                  @view-changed="onViewChanged"
                />
              </div>
            </template>
          </HotelsSplitView>
        </div>
        <template #fallback>
          <div class="map-view-inner">
            <div class="map-loading">
              <div class="loading-spinner"></div>
              <p>Loading map view...</p>
            </div>
          </div>
        </template>
      </ClientOnly>
    </div>
  </div>
</template>

<script setup lang="ts">
const { $siteConfig } = useNuxtApp()
import {useSearchStore} from "~/store/search";
import { normalizePriceValue } from "~/utils/currency";
import { defineAsyncComponent } from 'vue'
import ViewToggle from "~/components/MarketPage/ViewToggle.vue";
import Sorting from "~/components/MarketPage/Sorting.vue";
import ProgressBar from "primevue/progressbar";
import DialogCloseOutside from "~/components/DialogCloseOutside.vue";
import { useHreflangTags, useBreadcrumbSchema, useFaqSchema } from '~/composables/useSeo';

const HotelsList = defineAsyncComponent(() => import('~/components/MarketPage/HotelsList.vue'))
const HotelsFilters = defineAsyncComponent(() => import('~/components/MarketPage/HotelsFilters/HotelsFilters.vue'))
const HotelsFiltersMobile = defineAsyncComponent(() => import('~/components/MarketPage/HotelsFilters/HotelsFiltersMobile.vue'))
const HotelsSplitView = defineAsyncComponent(() => import('~/components/MarketPage/HotelsSplitView.vue'))
const searchStore = useSearchStore();
const selectedCity = computed(() => searchStore.selectedCity)
const runtimeConfig = useRuntimeConfig()
const route = useRoute()

// --- SSR: Fetch city hotel info for SEO content ---
const hotelCitySlug = route.params.city as string
const { locale } = useI18n()
const { data: _hotelCityInfo } = await useAsyncData(
  `hotel-city-info-${hotelCitySlug}-${locale.value}`,
  () => $fetch<any>(`${runtimeConfig.public.baseURL}/api/product/v1/hotels/city-info/${hotelCitySlug}`, {
    headers: { 'Accept-Language': locale.value }
  }).catch(() => null)
)
const hotelCityInfo = computed(() => _hotelCityInfo.value ?? null)

const hotelsLoading = ref({ value: 0, isFinished: false })
const showMobileFiltersDialog = ref(false)
// Check if map query parameter is present to show map view by default
const currentView = ref(route.query.map === 'true' ? 'map' : 'list')

const MIN_MAP_HEIGHT = 320
const mapViewportHeight = ref('calc(100vh - 176px)')
const hotelsSplitViewRef = ref<any | null>(null)
let headerResizeObserver: ResizeObserver | null = null
let searchPanelResizeObserver: ResizeObserver | null = null
let resizeListener: (() => void) | null = null

const getHotelPriceValue = (hotelProduct: any): number | null => {
  if (!hotelProduct) {
    return null
  }

  const candidates = [
    hotelProduct.cheapest_rate?.rate,
    hotelProduct.cheapest_rate?.night_rate?.amount,
    hotelProduct.cheapest_rate?.night_rate?.converted?.amount,
    hotelProduct.price_from?.amount,
    hotelProduct.price_from?.converted?.amount,
  ]

  for (const candidate of candidates) {
    const parsed = normalizePriceValue(candidate)
    if (parsed !== null) {
      return parsed
    }
  }

  return null
}

const triggerMapResize = () => {
  if (process.server || currentView.value !== 'map') {
    return
  }

  const splitViewInstance = hotelsSplitViewRef.value as any
  if (splitViewInstance && typeof splitViewInstance.invalidateMapSize === 'function') {
    requestAnimationFrame(() => {
      splitViewInstance.invalidateMapSize()
    })
  }
}

const updateMapHeight = () => {
  if (process.server) {
    return
  }

  const viewportHeight = window.innerHeight || 0
  const mapContainerEl = document.querySelector('.map-view-full-width') as HTMLElement | null

  let computedHeight: number | null = null

  if (mapContainerEl) {
    const { top } = mapContainerEl.getBoundingClientRect()
    const clampedTop = Math.max(top, 0)
    computedHeight = viewportHeight - clampedTop
  }

  if (computedHeight === null || Number.isNaN(computedHeight)) {
    const headerEl = document.querySelector('.site-header-wrapper') as HTMLElement | null
    const searchPanelEl = document.querySelector('.search-panel-container') as HTMLElement | null
    const headerHeight = headerEl?.getBoundingClientRect().height || 0
    const searchPanelHeight = searchPanelEl?.getBoundingClientRect().height || 0
    computedHeight = viewportHeight - headerHeight - searchPanelHeight
  }

  computedHeight = Math.max(computedHeight, MIN_MAP_HEIGHT)
  mapViewportHeight.value = `${computedHeight}px`
  triggerMapResize()
}

const scheduleMapHeight = () => {
  if (process.server) {
    return
  }

  requestAnimationFrame(() => {
    updateMapHeight()
  })
}

// Map-related reactive data
const mapCenter = computed(() => {
  if (searchStore.selectedCity?.latitude && searchStore.selectedCity?.longitude) {
    return {
      lat: parseFloat(searchStore.selectedCity.latitude),
      lng: parseFloat(searchStore.selectedCity.longitude)
    }
  }
  return { lat: 40.7128, lng: -74.0060 } // Default to NYC
})

// POI location for map marker (when user searched for a landmark/POI)
const poiLocation = computed(() => {
  // Check route query params first (from URL)
  if (route.query.poi_lat && route.query.poi_lon) {
    return {
      lat: parseFloat(route.query.poi_lat as string),
      lng: parseFloat(route.query.poi_lon as string),
      name: route.query.poi_name as string || 'Selected Location'
    }
  }
  // Check selectedCity for POI info (from in-memory state)
  if (searchStore.selectedCity?.poiLatitude && searchStore.selectedCity?.poiLongitude) {
    return {
      lat: parseFloat(searchStore.selectedCity.poiLatitude),
      lng: parseFloat(searchStore.selectedCity.poiLongitude),
      name: searchStore.selectedCity.poiName || 'Selected Location'
    }
  }
  return null
})

const mapHotels = computed(() => {
  // Get hotels from both sources - need to combine them like the list view does
  const hotelsFromProducts = searchStore.hotelsProducts || []
  const hotelsFromFirstPage = searchStore.hotelProductsFirstPage || []

  // Flatten the hotel products first page if it's an array of arrays (scored hotels)
  let flatFirstPageHotels: any[] = []
  if (Array.isArray(hotelsFromFirstPage) && hotelsFromFirstPage.length > 0) {
    if (Array.isArray(hotelsFromFirstPage[0])) {
      // It's nested arrays (chunks), flatten them
      flatFirstPageHotels = hotelsFromFirstPage.flat()
    } else {
      flatFirstPageHotels = hotelsFromFirstPage
    }
  }

  // Combine both sources: scored hotels from first page + plain hotels from products
  // This matches what HotelsList.vue does
  const flatHotels = [...flatFirstPageHotels, ...hotelsFromProducts]

  const mappedHotels = flatHotels.map((hotelProduct: any) => {
    // Try different possible property names for coordinates
    const lat = hotelProduct.hotel?.latitude || 
                hotelProduct.hotel?.lat || 
                hotelProduct.latitude || 
                hotelProduct.lat ||
                hotelProduct.hotel?.location?.latitude ||
                hotelProduct.hotel?.location?.lat ||
                hotelProduct.hotel?.coordinates?.latitude ||
                hotelProduct.hotel?.coordinates?.lat ||
                hotelProduct.hotel?.geo?.latitude ||
                hotelProduct.hotel?.geo?.lat

    const lng = hotelProduct.hotel?.longitude || 
                hotelProduct.hotel?.lng || 
                hotelProduct.longitude || 
                hotelProduct.lng ||
                hotelProduct.hotel?.location?.longitude ||
                hotelProduct.hotel?.location?.lng ||
                hotelProduct.hotel?.coordinates?.longitude ||
                hotelProduct.hotel?.coordinates?.lng ||
                hotelProduct.hotel?.geo?.longitude ||
                hotelProduct.hotel?.geo?.lng

    return {
      id: hotelProduct.hotel?.id,
      name: hotelProduct.hotel?.name,
      lat: lat ? parseFloat(lat.toString()) : 0,
      lng: lng ? parseFloat(lng.toString()) : 0,
      address: hotelProduct.hotel?.address,
      rating: hotelProduct.hotel?.rating || hotelProduct.hotel?.avg_rating,
      price: getHotelPriceValue(hotelProduct) ?? 0,
      image: hotelProduct.hotel?.featured_image || hotelProduct.hotel?.cover_image?.url,
      slug: hotelProduct.hotel?.slug,
      // Add full pricing objects for map bubble
      cheapestRate: hotelProduct.cheapest_rate,
      priceFrom: hotelProduct.price_from,
      // Add debug info
      rawLat: lat,
      rawLng: lng
    }
  })

  // Filter invalid coordinates
  const validMappedHotels = mappedHotels.filter((hotel) => {
    const hasValidCoords = hotel.lat && hotel.lng && hotel.lat !== 0 && hotel.lng !== 0 && !isNaN(hotel.lat) && !isNaN(hotel.lng)
    return hasValidCoords
  })

  // Add test hotels if no valid coordinates are found and we have hotels
  if (validMappedHotels.length === 0 && flatHotels.length > 0) {
    const testHotels = flatHotels.slice(0, 3).map((hotelProduct: any, index: number) => ({
      id: hotelProduct.hotel?.id || `test-${index}`,
      name: hotelProduct.hotel?.name || `Test Hotel ${index + 1}`,
      lat: 40.7128 + (index * 0.01), // NYC area with slight offset
      lng: -74.0060 + (index * 0.01),
      address: hotelProduct.hotel?.address || 'Test Address',
      rating: hotelProduct.hotel?.rating || hotelProduct.hotel?.avg_rating || 4,
      price: getHotelPriceValue(hotelProduct) ?? 100,
      image: hotelProduct.hotel?.featured_image || hotelProduct.hotel?.cover_image?.url,
      slug: hotelProduct.hotel?.slug || `test-hotel-${index}`
    }))
    return testHotels
  }

  return validMappedHotels
})
const facebookImg = (selectedCity.value?.slug || route.params.city || 'default-site-img') + '.jpg'
const breadcrumbs = computed(() => {
  const items: Array<{label: string, url?: string}> = [
    { label: 'Home', url: '/' },
  ]

  const countryName = searchStore.selectedCity?.country_name
  const countryCode = searchStore.selectedCity?.country_code
  if (countryName && countryCode) {
    items.push({ label: countryName, url: `/hotels/destinations/${countryCode.toLowerCase()}` })
  }

  items.push({ label: searchStore.selectedCity?.name || 'City' })
  return items
})

useJsonld(() => useBreadcrumbSchema(breadcrumbs.value, runtimeConfig.public.baseURL))

const dataIsLoading = ref(true)

//onMounted
onMounted(async () => {
  if (process.client) {
    scheduleMapHeight()
    resizeListener = () => scheduleMapHeight()
    window.addEventListener('resize', resizeListener)

    if (typeof ResizeObserver !== 'undefined') {
      const headerEl = document.querySelector('.site-header-wrapper')
      if (headerEl) {
        headerResizeObserver = new ResizeObserver(() => scheduleMapHeight())
        headerResizeObserver.observe(headerEl)
      }

      const searchPanelEl = document.querySelector('.search-panel-container')
      if (searchPanelEl) {
        searchPanelResizeObserver = new ResizeObserver(() => scheduleMapHeight())
        searchPanelResizeObserver.observe(searchPanelEl)
      }
    }
  }

  // Determine if we're in dynamic (with dates) or static (without dates) mode
  const hasDates = route.query.date_start && route.query.date_end
  searchStore.isDynamicHotels = !!hasDates

  // Only clear results if this is a genuinely new page load, not an internal navigation
  const hasExistingResults = searchStore.hotelsProducts?.length > 0 || searchStore.hotelProductsFirstPage?.length > 0
  const cityMatches = searchStore.selectedCity?.slug === route.params.city
  
  if (!hasExistingResults || !cityMatches) {
    searchStore.clearHotelsResults()
  }
  // If loading a different city, ensure searchPending is not stuck from a prior aborted search.
  // Safe: the "missing results" watcher requires dateStart && dateEnd && cityId — for static mode
  // (no dates) it won't fire; for dynamic mode, onMounted's searchHotels will supersede it.
  if (!cityMatches) {
    searchStore.searchPending = false
  }

  queryAndIndeterminate()

  if (!searchStore.searchPending) {
    // Get city data (skip for POI-based searches where city may not exist in DB)
    const isPOISearch = searchStore.selectedCity?.isPoiSearch || route.query.poi_lat
    if (!isPOISearch && (!searchStore.selectedCity || !cityMatches)) {
      try {
        await searchStore.getCity(route.params.city as string)
      } catch (error) {
        // City not found - might be a POI-based slug, continue with coordinates if available
        console.warn('City not found in DB:', route.params.city)
      }
    }

    // Load hotels based on mode
    if (hasDates) {
      // Dynamic mode: load hotels with availability and pricing
      await searchStore.searchHotels(route.query)
    } else if (!(searchStore.hotelsProducts?.length > 0 || searchStore.hotelProductsFirstPage?.length > 0)) {
      // Static mode: load hotels without dates (just hotel data, no pricing)
      await searchStore.searchStaticHotels({
        citySlug: route.params.city,
        ...route.query
      })
    }
  }
  
  // If page loads in map view (from URL ?map=true), auto-load all hotels
  if (currentView.value === 'map') {
    await nextTick()
    await loadAllRemainingHotels()
  }
})


//methods
function openSearchPanel() {
  // Trigger the search panel to open (this will be handled by the layout)
  // The search panel is in the layout, so we emit an event or use a global state
  if (process.client) {
    const searchPanel = document.querySelector('.search-panel-toggle-button')
    if (searchPanel) {
      (searchPanel as HTMLElement).click()
    }
  }
}

function onViewChanged(view: string) {
  currentView.value = view
  
  // Update URL query parameter to persist view preference
  const query = { ...route.query }
  if (view === 'map') {
    query.map = 'true'
  } else {
    delete query.map
  }
  
  // Update URL without triggering a full page reload
  navigateTo({
    path: route.path,
    query: query
  }, { replace: true })
}

function onHotelSelected(hotel: any) {
  // Handle hotel selection from map
  // You could show a side panel with hotel details or navigate to hotel page
}

function onMapMoved(mapData: any) {
  // Handle map movement - could trigger search for hotels in new area
}

async function loadAllRemainingHotels() {
  // Static mode: load all pages for map
  if (!searchStore.isDynamicHotels) {
    if (!searchStore.loadingMoreHotelsProducts) {
      await searchStore.loadAllStaticHotelsForMap(route.query)
    }
    return
  }

  // Dynamic mode: keep fetching via streaming token
  if (!searchStore.hotelMoreIndicator) {
    return
  }

  let attempts = 0
  const maxAttempts = 50

  while (searchStore.hotelMoreIndicator && attempts < maxAttempts) {
    if (searchStore.loadingMoreHotelsProducts) {
      // Wait for current load to finish before continuing
      await new Promise(resolve => setTimeout(resolve, 500))
      continue
    }

    attempts++

    try {
      const currentQuery = { ...route.query }
      await searchStore.searchHotels({
        ...currentQuery,
        token: searchStore.hotelMoreIndicator
      })
    } catch (error) {
      console.error('[Map View] Error loading more hotels:', error)
      break
    }
  }
}

watch(currentView, async (view) => {
  if (view === 'map') {
    await nextTick()
    scheduleMapHeight()

    // Auto-load all remaining hotels for map view
    await loadAllRemainingHotels()
  }
})

watch(() => searchStore.searchPending, async (isPending, wasPending) => {
  if (wasPending && !isPending && currentView.value === 'map') {
    await nextTick()
    scheduleMapHeight()
  }
})

function queryAndIndeterminate(value = 0) {
  hotelsLoading.value.isFinished = !searchInProgress.value
  if (hotelsLoading.value.isFinished) {
    hotelsLoading.value.value = 100

    setTimeout(() => {
      dataIsLoading.value = false
    }, 1000)
    return
  }

  hotelsLoading.value.value = value
  // setTimeout(() => {
  //   if (hotelsLoading.value.value < 70) {
  //     dataIsLoading.value = true
  //     queryAndIndeterminate(hotelsLoading.value.value + 5)
  //   }
  // }, 1000)
}

//computed loadingMoreHotelsProducts
const searchInProgress = computed(() => searchStore.searchInProgress)

async function onRetrySearch() {
  dataIsLoading.value = true
  hotelsLoading.value = { value: 0, isFinished: false }
  const { token, next, ...cleanQuery } = route.query as Record<string, any>
  await searchStore.searchHotels({ ...cleanQuery })
}


definePageMeta({
  layout: 'search'
})

// SSR-friendly SEO meta tags - computed for reactivity
const pageTitle = computed(() => {
  const cityName = selectedCity.value?.name || ''
  const brand = $siteConfig?.value.brand || 'Tripening'
  return cityName 
    ? `Hotels in ${cityName} - Compare & Save | ${brand}`
    : `Find Hotels - Best Rates & Deals | ${brand}`
})

const pageDescription = computed(() => {
  const cityName = selectedCity.value?.name || hotelCityInfo.value?.city_name || ''
  const brand = $siteConfig?.value.brand || 'Tripening'

  // Prefer AI-generated meta description
  if (hotelCityInfo.value?.ai_meta_description) return hotelCityInfo.value.ai_meta_description

  return cityName
    ? `Find the best hotels in ${cityName}. Compare prices, read verified reviews, and save up to 10%. No hidden fees, best price guaranteed on ${brand}.`
    : `Search and compare hotels worldwide on ${brand}. Get exclusive rates and save money.`
})

const canonicalUrl = computed(() => {
  const citySlug = selectedCity.value?.slug || route.params.city || 'results'
  return `${runtimeConfig.public.baseURL}/hotels/${citySlug}`
})

// Use useHead with computed values for SSR
useHead({
  title: pageTitle,
  link: [
    { rel: 'canonical', href: canonicalUrl }
  ],
  meta: [
    { name: 'description', content: pageDescription },
    { property: 'og:title', content: pageTitle },
    { property: 'og:description', content: pageDescription },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:image', content: computed(() => `${runtimeConfig.public.baseURL}/images/cities/${selectedCity.value?.slug || 'default'}.jpg`) },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: computed(() => $siteConfig?.value.brand || 'Tripening') },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: pageTitle },
    { name: 'twitter:description', content: pageDescription }
  ]
})

// Add hreflang tags for multi-language SEO
useHreflangTags()

// Add noindex for heavily filtered pages to prevent duplicate content
const filterParams = [
  'sort', 'rating_gte', 'free_cancellation', 'board_plans', 'pay_at_property_only',
  'hotel_name', 'price_gte', 'price_lte', 'amenities', 'pay_now_only',
  'min_rating', 'min_reviews', 'distance_band', 'neighborhoods', 'amenity_combos',
  'custom_location', 'provider', 'map'
]
const hasFilters = computed(() => {
  // Check if any filter parameters are present (excluding page 1 and dates)
  const pageNum = Number(route.query.page) || 1
  const hasFilterParams = filterParams.some(param => route.query[param])
  const hasPagination = pageNum > 1
  return hasFilterParams || hasPagination
})

// Dynamically add noindex meta tag when filters are applied
useHead({
  meta: computed(() => hasFilters.value ? [{ name: 'robots', content: 'noindex, follow' }] : [])
})

// Add ItemList structured data for hotel listings
useJsonld(() => {
  const hotels = searchStore.hotelsProducts || searchStore.hotelProductsFirstPage || []
  const flatHotels = hotels.flat()
  const cityName = selectedCity.value?.name || ''
  const citySlugVal = selectedCity.value?.slug || route.params.city || 'results'
  const baseUrl = runtimeConfig.public.baseURL

  if (!flatHotels.length) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'name': `Hotels in ${cityName}`,
    'numberOfItems': flatHotels.length,
    'itemListElement': flatHotels.slice(0, 20).map((hotelProduct: any, index: number) => {
      const hotel = hotelProduct.hotel || hotelProduct
      return {
        '@type': 'ListItem',
        'position': index + 1,
        'item': {
          '@type': 'Hotel',
          'name': hotel.name,
          'url': `${baseUrl}/hotels/${citySlugVal}/${hotel.id}-${hotel.slug}`,
          ...(hotel.address && { 'address': hotel.address }),
          ...(hotel.rating && { 'starRating': { '@type': 'Rating', 'ratingValue': hotel.rating } })
        }
      }
    })
  }
})

// TouristDestination JSON-LD (from SSR city info)
useJsonld(() => {
  const c = hotelCityInfo.value
  if (!c) return null
  const cityName = c.city_name || selectedCity.value?.name || ''

  return {
    '@context': 'https://schema.org',
    '@type': 'TouristDestination',
    'name': cityName,
    'description': pageDescription.value,
  }
})

// FAQ JSON-LD (from AI content)
useJsonld(() => {
  const faqItems = hotelCityInfo.value?.ai_faq
  if (!faqItems?.length) return null
  return useFaqSchema(faqItems.map((item: any) => ({ title: item.question, text: item.answer })))
})

watch(searchInProgress, (newValue, oldValue) => {
  hotelsLoading.value.isFinished = !newValue
  if (newValue === oldValue) return

  queryAndIndeterminate()
})

// Watch for route params changes (city change)
watch(() => route.params.city, async (newCity, oldCity) => {
  if (newCity && newCity !== oldCity && oldCity) {
    console.log('[Hotels Page] City changed from', oldCity, 'to', newCity)
    searchStore.clearHotelsResults()
    dataIsLoading.value = true
    hotelsLoading.value = { value: 0, isFinished: false }
    
    try {
      await searchStore.getCity(newCity as string)
      const hasDates = route.query.date_start && route.query.date_end
      searchStore.isDynamicHotels = !!hasDates
      
      if (hasDates) {
        await searchStore.searchHotels(route.query)
      } else {
        await searchStore.searchStaticHotels({ citySlug: newCity, ...route.query })
      }
    } catch (error) {
      console.error('[Hotels Page] Failed to load city hotels:', error)
    }
  }
}, { immediate: false })

// Watch for query changes (dates, filters, etc.)
watch(() => route.query, async (newQuery, oldQuery) => {
  // Check if dates changed
  const oldHasDates = oldQuery?.date_start && oldQuery?.date_end
  const newHasDates = newQuery?.date_start && newQuery?.date_end
  const datesChanged = oldQuery?.date_start !== newQuery?.date_start || oldQuery?.date_end !== newQuery?.date_end
  
  // Update dynamic/static mode
  searchStore.isDynamicHotels = !!newHasDates
  
  // If switching between static and dynamic mode, or if filters/dates changed
  if ((!oldHasDates && newHasDates) || (oldHasDates && !newHasDates) || datesChanged) {
    console.log('[Hotels Page] Search mode changed or dates updated')
    dataIsLoading.value = true
    hotelsLoading.value = { value: 0, isFinished: false }
    
    if (newHasDates) {
      // Switch to dynamic search
      await searchStore.searchHotels(newQuery)
    } else if (searchStore.selectedCity) {
      // Switch to static search
      await searchStore.searchStaticHotels({ citySlug: route.params.city, ...newQuery })
    }
  } else if (newHasDates && searchStore.isDynamicHotels) {
    // Filters changed in dynamic mode
    await searchStore.searchHotels(newQuery)
  } else if (!newHasDates && !searchStore.isDynamicHotels) {
    // Filters changed in static mode (rating, name search, sorting)
    const filterKeys = ['rating_gte', 'hotel_name', 'sort']
    const filtersChanged = filterKeys.some(key => oldQuery?.[key] !== newQuery?.[key])

    if (filtersChanged) {
      await searchStore.searchStaticHotels({ citySlug: route.params.city, ...newQuery })
    }
  }
}, { immediate: false, deep: true })

// Watch for when results disappear but we have valid search params
watch([
  () => searchStore.hotelsProducts?.length || 0,
  () => searchStore.hotelProductsFirstPage?.length || 0,
  () => searchStore.searchPending,
  () => route.query.date_start,
  () => route.query.date_end,
  () => searchStore.selectedCity?.id
], async ([productsLen, firstPageLen, isPending, dateStart, dateEnd, cityId]) => {
  const hasResults = productsLen > 0 || firstPageLen > 0
  const hasValidParams = dateStart && dateEnd && cityId
  const notSearching = !isPending
  // Skip retry when provider filter is active - 0 results is valid for filtered searches
  const hasProviderFilter = !!route.query.provider
  // Skip retry when search already failed with an error (e.g. 422 past dates) to prevent infinite loop
  const hasSearchError = !!searchStore.hotelSearchError

  if (!hasResults && hasValidParams && notSearching && !hasProviderFilter && !hasSearchError) {
    console.log('[Hotels Page] Results missing, triggering search. Params:', {
      dateStart,
      dateEnd,
      cityId,
      productsLen,
      firstPageLen
    })

    // Small delay to avoid race conditions with other watchers
    await nextTick()
    if (!searchStore.searchPending) {
      await searchStore.searchHotels(route.query)
    }
  }
}, { immediate: false })

onBeforeUnmount(() => {
  if (process.server) {
    return
  }

  if (resizeListener) {
    window.removeEventListener('resize', resizeListener)
    resizeListener = null
  }
  headerResizeObserver?.disconnect()
  searchPanelResizeObserver?.disconnect()
})
</script>

<style>
.page-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.list-layout {
  display: flex;
  gap: 24px;
}

.results-listing-wrapper {
  width: 100%;
}

.results-listing-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }

  h1 {
    color: var(--black);
    font-size: 20px;
    font-weight: 600;
    line-height: 120%;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .header-controls {
    display: flex;
    align-items: center;
    gap: 12px;

    @media screen and (max-width: 768px) {
      justify-content: space-between;
      width: 100%;
    }
  }
}

.show-desktop {
  @media screen and (max-width: 1024px) {
    display: none !important;
  }
}

.show-not-desktop {
  display: none;
  @media screen and (max-width: 1024px) {
    display: flex !important;
  }
}

.hotel-error-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  background-color: color-mix(in srgb, var(--red) 8%, transparent);
  color: var(--red);
}

.static-hotels-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 16px 20px;
  border-radius: 8px;
  background-color: var(--blue-highlight-additional);
  color: var(--blue);
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--blue-highlight);
    transform: translateY(-1px);
  }

  svg {
    flex-shrink: 0;
  }

  @media screen and (max-width: 768px) {
    font-size: 14px;
    padding: 12px 16px;
  }
}

.sidebar-wrapper {
  display: flex;
  flex-direction: column;

  @media screen and (max-width: 1024px) {
    display: none;
  }
}

.floating-view-toggle {
  display: inline-flex;
  align-items: center;
  background: var(--white);
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  padding: 4px;
  margin: 8px 0;
  position: relative;
  z-index: 10;

  &.in-list-view {
    margin: 16px 0 12px;
  }

  &.in-map-view {
    margin: 4px 0;
  }
}

.map-view-header-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 24px;
  margin-top: 8px;
  position: relative;
  z-index: 100;
  background: var(--white);

  @media screen and (max-width: 1024px) {
    padding: 0 16px;
  }
}

.map-view-breadcrumbs-inline {
  @media screen and (max-width: 768px) {
    display: none;
  }
}

.map-view-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  @media screen and (max-width: 768px) {
    gap: 12px;
  }

  .map-header-left {
    flex: 1;
    min-width: 0;

    @media screen and (max-width: 768px) {
      flex: 1 1 100%;
    }
  }

  h1 {
    color: var(--black);
    font-size: 20px;
    font-weight: 600;
    line-height: 120%;
    margin: 0;

    @media screen and (max-width: 768px) {
      font-size: 18px;
    }

    span {
      @media screen and (max-width: 480px) {
        display: block;
        font-size: 14px;
        font-weight: 400;
        color: var(--dark-grey-secondary);
        margin-top: 4px;
      }
    }
  }

  .map-header-controls {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;

    @media screen and (max-width: 768px) {
      flex: 1 1 100%;
      justify-content: space-between;
    }
  }
}

.map-view-container {
  width: 100%;
  min-height: 600px;
  position: relative;
}

.loading-indicator {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  opacity: 0.6;
  animation: fadeIn 0.5s ease-in;
}

.loading-dots {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.loading-dots .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: var(--blue);
  opacity: 0.3;
  animation: pulse 1.4s infinite ease-in-out;
}

.loading-dots .dot:nth-child(1) {
  animation-delay: 0s;
}

.loading-dots .dot:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots .dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes pulse {
  0%, 80%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  40% {
    opacity: 1;
    transform: scale(1.2);
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.map-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 100%;
  background: var(--light-grey);
  border-radius: 8px;
  border: 1px solid var(--grey-border);
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--grey-border);
  border-top: 3px solid var(--blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.map-view-full-width {
  width: 100vw;
  position: relative;
  left: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding-top: 8px;

  @media screen and (max-width: 1024px) {
    padding-top: 6px;
  }
}

.map-view-inner {
  flex: 1;
  height: 100%;
  display: flex;
}

.map-view-inner > * {
  flex: 1;
  height: 100%;
}

.page-container.map-view-active {
  gap: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
}

.seo-content-section {
  padding: 0 0 40px;
}

.cross-link-section {
  margin-top: 24px;
}

.cross-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--blue);
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  padding: 10px 16px;
  border: 1px solid var(--blue);
  border-radius: 8px;
  transition: background-color 0.15s;
}

.cross-link:hover {
  background-color: var(--blue-highlight-additional);
}
</style>
